import React from "react";
import { Truck, Storefront, Calendar } from "@phosphor-icons/react";
import {
  deliveryBadge, formatDeliveryDate, formatFulfillmentType, BADGE_STYLES,
} from "@/lib/orderFields";
import SpecialtyChip, { SpecialtyPaymentChip } from "@/components/order/SpecialtyChip";

/**
 * Compact row of order metadata chips:
 *   [Date badge] [Fulfillment chip] [Store chip]
 * Each chip renders only if the underlying field is present.
 * Used on Orders list cards and Dashboard cards.
 */
export default function OrderMetaChips({ order, size = "sm" }) {
  if (!order) return null;

  const badge = deliveryBadge(order.delivery_date);
  const dateText = formatDeliveryDate(order.delivery_date);
  const fulfill = formatFulfillmentType(order.fulfillment_type);
  const store = order.store;
  const hasSpecialty = !!order.has_specialty_items;
  const hasSpecialtyPayment = !!order.specialty_payment_status;

  if (!badge && !fulfill && !store && !hasSpecialty && !hasSpecialtyPayment) return null;

  const px = size === "md" ? "px-2.5" : "px-2";
  const py = size === "md" ? "py-1" : "py-0.5";
  const txt = size === "md" ? "text-[11px]" : "text-[10px]";

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="order-meta-chips">
      {badge && (
        <span
          data-testid="meta-date-badge"
          className={`inline-flex items-center gap-1 rounded border font-bold uppercase tracking-[0.15em] ${px} ${py} ${txt} whitespace-nowrap`}
          style={{
            background: BADGE_STYLES[badge.tone].bg,
            color: BADGE_STYLES[badge.tone].text,
            borderColor: BADGE_STYLES[badge.tone].border,
          }}
          title={dateText || badge.label}
        >
          <Calendar size={11} weight="bold" />
          {badge.label}
          {dateText && size === "md" && (
            <span className="font-normal opacity-80 ml-1">· {dateText}</span>
          )}
        </span>
      )}

      {fulfill && (
        <span
          data-testid="meta-fulfillment-chip"
          className={`inline-flex items-center gap-1 rounded border ${px} ${py} ${txt} font-semibold whitespace-nowrap ${
            fulfill === "Pickup"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
              : "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
          }`}
        >
          {fulfill === "Pickup"
            ? <Storefront size={11} weight="bold" />
            : <Truck size={11} weight="bold" />}
          {fulfill}
        </span>
      )}

      {store && (
        <span
          data-testid="meta-store-chip"
          className={`inline-flex items-center gap-1 rounded border ${px} ${py} ${txt} font-semibold whitespace-nowrap bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700`}
          title={store}
        >
          <Storefront size={11} weight="bold" />
          <span className="truncate max-w-[140px]">{store}</span>
        </span>
      )}

      <SpecialtyChip order={order} size={size} />
      <SpecialtyPaymentChip order={order} size={size} />
    </div>
  );
}
